import React, { useState, useMemo } from 'react';
import { format, addMonths, subMonths, addDays, startOfMonth } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../ui/button';
import { MonthView } from './MonthView';
import { WeekView } from './WeekView';
import { weekDays } from './calendarUtils';
import { CalendarFilters, EMPTY_FILTERS, applyCalendarFilters } from './CalendarFilters';

/**
 * Shared delivery calendar (month bars + hour-grid week view, hover cards, PM/Trainer/Client filters).
 * events: [{ id, title, start, end, startTime, endTime, color, statusLabel, trainers[], pm, client, muted, onClick }]
 */
export const DeliveryCalendar = ({ events = [], legend = [], toolbarExtra = null, testId = 'delivery-calendar' }) => {
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState('month');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const filtered = useMemo(() => applyCalendarFilters(events, filters), [events, filters]);

  const step = (dir) => setCursor((c) => (view === 'month' ? (dir > 0 ? addMonths(startOfMonth(c), 1) : subMonths(startOfMonth(c), 1)) : addDays(c, 7 * dir)));
  const wk = weekDays(cursor);
  const label = view === 'month' ? format(cursor, 'MMMM yyyy') : `${format(wk[0], 'MMM d')} – ${format(wk[6], 'MMM d, yyyy')}`;

  return (
    <div className="rounded-xl border bg-card p-4 space-y-3" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => step(-1)} data-testid={`${testId}-prev`}><ChevronLeft className="w-4 h-4" /></Button>
          <h2 className="text-base font-semibold min-w-[190px] text-center" data-testid={`${testId}-label`}>{label}</h2>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => step(1)} data-testid={`${testId}-next`}><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setCursor(new Date())} data-testid={`${testId}-today`}>Today</Button>
        </div>
        <div className="flex items-center rounded-full bg-muted p-0.5" data-testid={`${testId}-view-toggle`}>
          {['month', 'week'].map((v) => (
            <button key={v} type="button" onClick={() => setView(v)}
              className={`px-3.5 py-1 text-xs font-medium rounded-full capitalize transition-colors ${view === v ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`${testId}-view-${v}`}>{v}</button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CalendarFilters events={events} filters={filters} onChange={setFilters} testId={testId} />
        {toolbarExtra}
      </div>
      {view === 'month'
        ? <MonthView cursor={cursor} events={filtered} testId={testId} onMore={(d) => { setCursor(d); setView('week'); }} />
        : <WeekView cursor={cursor} events={filtered} testId={testId} />}
      {legend.length > 0 && (
        <div className="flex items-center gap-4 pt-1 flex-wrap" data-testid={`${testId}-legend`}>
          {legend.map((l) => (
            <div key={l.label} className="flex items-center gap-1.5">
              <span className={`w-3 h-3 rounded ${l.dashed ? 'border border-dashed border-muted-foreground/50' : ''}`} style={{ background: l.color }} />
              <span className="text-xs text-muted-foreground">{l.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DeliveryCalendar;
